import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Container, Spinner, Table } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { PurchaseOrderPaper } from './PurchaseOrderDocument';
import CatalogView from './CatalogView';

const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const qty = (v) => (v == null ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 3 }));

/**
 * What a customer sees when they open a link sent on WhatsApp or by email: a statement of their dues, an invoice or a
 * quotation from the business (or, for a vendor, a purchase order), with a "Pay by UPI" button and QR code for what is due when the business has a UPI ID. No login; the
 * link is the only key.
 */
function SharedPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 });
    return (v) => f.format(Number(v || 0));
  }, []);

  useEffect(() => {
    document.title = 'iBigDo';
    // Plain fetch: nothing about a signed-in user goes with it
    fetch(`/api/public/shared/${encodeURIComponent(token)}`, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.message || 'This link could not be opened.');
        return body;
      })
      .then((body) => {
        setData(body);
        const what = body.kind === 'SALE_BILL' ? `Invoice ${body.billNumber}` : body.kind === 'QUOTATION' ? `Quotation ${body.quoteNumber}`
          : body.kind === 'PURCHASE_ORDER' ? `Purchase order ${body.poNumber}` : body.kind === 'CATALOG' ? 'Product catalog' : 'Statement';
        document.title = `${what} from ${body.organization?.name || ''}`.trim();
      })
      .catch((e) => setError(e.message));
  }, [token]);

  if (error) {
    return (
      <Container className="py-5" style={{ maxWidth: 640 }}>
        <Alert variant="warning">{error}</Alert>
      </Container>
    );
  }
  if (!data) {
    return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;
  }

  return (
    <Container className="py-4 shared-page" style={{ maxWidth: 820 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .shared-page { max-width: none !important; }
        }
      `}</style>
      {data.kind !== 'PURCHASE_ORDER' && <OrgHeader org={data.organization || {}} />}
      {data.kind === 'PURCHASE_ORDER' ? <PurchaseOrderPaper doc={data} />
        : data.kind === 'SALE_BILL' ? <InvoiceView data={data} money={money} />
        : data.kind === 'CATALOG' ? <CatalogView data={data} token={token} money={money} />
        : data.kind === 'QUOTATION' ? <QuotationView data={data} money={money} /> : <StatementView data={data} money={money} />}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3 small text-muted">
        <span>
          This link works until {day(data.expiresAt)}.
          {data.kind === 'SALE_BILL' ? ' What is due is as on today.'
            : data.kind === 'CATALOG' ? ' Products, prices and stock are as on the day you open it.'
            : data.kind === 'QUOTATION' || data.kind === 'PURCHASE_ORDER' ? '' : ' Amounts are as on today and change as bills are paid.'}
        </span>
        <Button variant="outline-secondary" size="sm" className="no-print" onClick={() => window.print()}>
          <i className="bi bi-download me-1" />Save as PDF
        </Button>
      </div>
      <div className="text-center small text-muted mt-4 no-print">Sent with iBigDo</div>
    </Container>
  );
}

function OrgHeader({ org }) {
  return (
    <div className="d-flex align-items-center gap-3 mb-3">
      {org.logoUrl && <img src={org.logoUrl} alt="" style={{ maxHeight: 48, maxWidth: 120 }} />}
      <div>
        <h1 className="h5 mb-0">{org.name}</h1>
        {org.legalName && org.legalName !== org.name && <div className="small">{org.legalName}</div>}
        <div className="small text-muted">{[org.address, org.gstin && `GSTIN ${org.gstin}`].filter(Boolean).join(' · ')}</div>
        {(org.phone || org.email) && <div className="small text-muted">{[org.phone, org.email].filter(Boolean).join(' · ')}</div>}
      </div>
    </div>
  );
}

function UpiCard({ upi, amount, orgName, money }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(upi.id);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };
  return (
    <Card className="mb-3 border-success no-print">
      <Card.Body className="d-flex flex-wrap gap-4 align-items-center">
        <div className="flex-grow-1">
          <div className="fw-semibold mb-2">Pay {money(amount)} by UPI</div>
          <Button variant="success" href={upi.uri} className="mb-2">Pay with a UPI app</Button>
          <div className="small text-muted">
            Opens Google Pay, PhonePe, Paytm or your bank&apos;s app on this phone. On a computer, scan the code with your phone.
          </div>
          <div className="small mt-2">
            UPI ID: <span className="font-monospace">{upi.id}</span>{' '}
            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
            {' '}· Paying to {upi.payee}
          </div>
          <div className="small text-muted mt-1">After paying, please send {orgName} the UPI reference number.</div>
        </div>
        <div className="bg-white p-2 rounded">
          <QRCodeSVG value={upi.uri} size={140} />
        </div>
      </Card.Body>
    </Card>
  );
}

function StatementView({ data, money }) {
  const bills = data.bills || [];
  const total = Number(data.total || 0);
  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="small text-muted text-uppercase">Statement of dues</div>
          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
            <div>
              <div className="fw-semibold">{data.customer?.name}</div>
              <div className="small text-muted">As on {day(data.asOf)}</div>
            </div>
            <div className="text-end">
              <div className="small text-muted">{total > 0 ? 'Total due' : 'Nothing due'}</div>
              <div className="fs-3 fw-bold">{money(total)}</div>
            </div>
          </div>
        </Card.Body>
      </Card>
      {data.upi && <UpiCard upi={data.upi} amount={total} orgName={data.organization?.name} money={money} />}
      {bills.length > 0 ? (
        <Table bordered responsive size="sm">
          <thead>
            <tr><th>Bill</th><th>Date</th><th>Due</th><th className="text-end">Amount due</th></tr>
          </thead>
          <tbody>
            {bills.map((b) => (
              <tr key={b.billNumber}>
                <td>{b.billNumber}</td>
                <td>{day(b.billDate)}</td>
                <td>
                  {day(b.dueDate)}
                  {b.daysOverdue > 0 && <span className="text-danger small ms-1">({b.daysOverdue} days overdue)</span>}
                </td>
                <td className="text-end">{money(b.balance)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><th colSpan={3} className="text-end">Total</th><th className="text-end">{money(total)}</th></tr>
          </tfoot>
        </Table>
      ) : (
        <Alert variant="success">Thank you. Nothing is due today.</Alert>
      )}
    </>
  );
}

function InvoiceView({ data, money }) {
  const t = data.totals || {};
  const lines = data.lines || [];
  const cancelled = data.status === 'CANCELLED';
  const due = Number(data.balanceDue || 0);
  const num = (v) => Number(v || 0);
  return (
    <>
      {cancelled && <Alert variant="danger">This invoice has been cancelled.</Alert>}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between gap-3">
            <div>
              <div className="small text-muted text-uppercase">Tax invoice</div>
              <div className="fs-5 fw-bold">{data.billNumber}</div>
              <div className="small">Date {day(data.billDate)}{data.dueDate && ` · Due ${day(data.dueDate)}`}</div>
              {data.placeOfSupply && <div className="small text-muted">Place of supply: {data.placeOfSupply}</div>}
            </div>
            <div className="text-end">
              <div className="small text-muted">{cancelled ? 'Cancelled' : due > 0 ? 'Amount due' : 'Paid'}</div>
              <div className="fs-3 fw-bold">{money(cancelled ? t.total : due > 0 ? due : t.total)}</div>
              {!cancelled && due > 0 && due !== num(t.total) && <div className="small text-muted">Invoice total {money(t.total)}; see below</div>}
              {!cancelled && due === 0 && <Badge bg="success">Paid, thank you</Badge>}
            </div>
          </div>
          <hr />
          <div className="small">
            <div className="text-muted">Billed to</div>
            <div className="fw-semibold">{data.customer?.name}</div>
            {data.customer?.gstin && <div>GSTIN {data.customer.gstin}</div>}
            {data.customer?.billingAddress && <div className="text-muted" style={{ whiteSpace: 'pre-line' }}>{data.customer.billingAddress}</div>}
            {data.customer?.shippingAddress && data.customer.shippingAddress !== data.customer.billingAddress && (
              <div className="mt-1"><span className="text-muted">Ship to: </span>{data.customer.shippingAddress}</div>
            )}
          </div>
        </Card.Body>
      </Card>

      {data.upi && <UpiCard upi={data.upi} amount={due} orgName={data.organization?.name} money={money} />}

      <Table bordered responsive size="sm" className="small">
        <thead>
          <tr>
            <th>Item</th><th>HSN</th><th className="text-end">Qty</th><th className="text-end">Rate</th>
            <th className="text-end">Taxable</th><th className="text-end">GST</th><th className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>{l.description}</td>
              <td>{l.hsn || ''}</td>
              <td className="text-end text-nowrap">{qty(l.quantity)} {l.unit || ''}</td>
              <td className="text-end">{money(l.rate)}</td>
              <td className="text-end">{money(l.taxable)}</td>
              <td className="text-end text-nowrap">{l.gstRate != null ? `${Number(l.gstRate)}% ` : ''}{money(l.tax)}</td>
              <td className="text-end">{money(l.total)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-end">
        <Table size="sm" className="small" style={{ maxWidth: 340 }}>
          <tbody>
            {num(t.discount) > 0 && <tr><td>Discount</td><td className="text-end">-{money(t.discount)}</td></tr>}
            <tr><td>Taxable value</td><td className="text-end">{money(t.taxable)}</td></tr>
            {num(t.cgst) > 0 && <tr><td>CGST</td><td className="text-end">{money(t.cgst)}</td></tr>}
            {num(t.sgst) > 0 && <tr><td>SGST</td><td className="text-end">{money(t.sgst)}</td></tr>}
            {num(t.igst) > 0 && <tr><td>IGST</td><td className="text-end">{money(t.igst)}</td></tr>}
            <tr className="fw-bold"><td>Total</td><td className="text-end">{money(t.total)}</td></tr>
            {!cancelled && num(data.noteAdjustment) !== 0 && (
              <tr>
                <td>{num(data.noteAdjustment) > 0 ? 'Debit notes' : 'Credit notes'}</td>
                <td className="text-end">{num(data.noteAdjustment) > 0 ? '+' : '-'}{money(Math.abs(num(data.noteAdjustment)))}</td>
              </tr>
            )}
            {!cancelled && num(data.paid) > 0 && <tr><td>Received</td><td className="text-end">-{money(data.paid)}</td></tr>}
            {!cancelled && <tr className="fw-bold"><td>Due</td><td className="text-end">{money(due)}</td></tr>}
          </tbody>
        </Table>
      </div>

      {data.eInvoice && (
        <Card className="mt-2">
          <Card.Body className="d-flex flex-wrap gap-3 align-items-center small">
            {data.eInvoice.signedQr && <div className="bg-white p-2 rounded"><QRCodeSVG value={data.eInvoice.signedQr} size={110} /></div>}
            <div>
              <div className="text-muted">e-Invoice</div>
              <div className="font-monospace text-break">IRN {data.eInvoice.irn}</div>
              <div>Ack no. {data.eInvoice.ackNo}</div>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
}

function QuotationView({ data, money }) {
  const t = data.totals || {};
  const lines = data.lines || [];
  const num = (v) => Number(v || 0);
  const expired = data.status === 'EXPIRED';
  return (
    <>
      {expired && <Alert variant="warning">This quotation was valid until {day(data.validUntil)}. Please ask for an updated one.</Alert>}
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between gap-3">
            <div>
              <div className="small text-muted text-uppercase">Quotation</div>
              <div className="fs-5 fw-bold">{data.quoteNumber}</div>
              <div className="small">Date {day(data.quoteDate)} · Valid until {day(data.validUntil)}</div>
            </div>
            <div className="text-end">
              <div className="small text-muted">Total (with GST)</div>
              <div className="fs-3 fw-bold">{money(t.total)}</div>
              {data.status === 'ACCEPTED' && <Badge bg="success">Accepted, thank you</Badge>}
            </div>
          </div>
          <hr />
          <div className="small">
            <div className="text-muted">For</div>
            <div className="fw-semibold">{data.customer?.name}</div>
            {data.customer?.gstin && <div>GSTIN {data.customer.gstin}</div>}
            {data.customer?.billingAddress && <div className="text-muted">{data.customer.billingAddress}</div>}
          </div>
        </Card.Body>
      </Card>

      <Table bordered responsive size="sm" className="small">
        <thead>
          <tr>
            <th>Item</th><th>HSN</th><th className="text-end">Qty</th><th className="text-end">Rate</th><th className="text-end">Disc.</th>
            <th className="text-end">Taxable</th><th className="text-end">GST</th><th className="text-end">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td>{l.description}</td>
              <td>{l.hsn || ''}</td>
              <td className="text-end text-nowrap">{qty(l.quantity)} {l.unit || ''}</td>
              <td className="text-end">{money(l.rate)}</td>
              <td className="text-end">{l.discountPercent != null ? `${Number(l.discountPercent)}%` : ''}</td>
              <td className="text-end">{money(l.taxable)}</td>
              <td className="text-end text-nowrap">{l.gstRate != null ? `${Number(l.gstRate)}% ` : ''}{money(l.tax)}</td>
              <td className="text-end">{money(l.total)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex flex-wrap justify-content-between gap-3">
        <div className="small" style={{ whiteSpace: 'pre-line', maxWidth: 420 }}>
          {data.notes && <><div className="text-muted">Terms and notes</div>{data.notes}</>}
        </div>
        <Table size="sm" className="small" style={{ maxWidth: 340 }}>
          <tbody>
            {num(t.discount) > 0 && <tr><td>Discount</td><td className="text-end">-{money(t.discount)}</td></tr>}
            <tr><td>Taxable value</td><td className="text-end">{money(t.taxable)}</td></tr>
            {num(t.cgst) > 0 && <tr><td>CGST</td><td className="text-end">{money(t.cgst)}</td></tr>}
            {num(t.sgst) > 0 && <tr><td>SGST</td><td className="text-end">{money(t.sgst)}</td></tr>}
            {num(t.igst) > 0 && <tr><td>IGST</td><td className="text-end">{money(t.igst)}</td></tr>}
            <tr className="fw-bold"><td>Total</td><td className="text-end">{money(t.total)}</td></tr>
          </tbody>
        </Table>
      </div>
    </>
  );
}

export default SharedPage;
